/* eslint-disable no-undef */
import React, { useState, useEffect } from 'react';
// 👇 引入了 QrCode 图标
import { Search, ShieldCheck, Menu, X, Lock, Plus, Trash2, LogOut, Clock, ChevronRight, FileText, AlertTriangle, Download, Mail, Users, MapPin, Phone, Scale, QrCode } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, onSnapshot, doc, setDoc, deleteDoc } from 'firebase/firestore';

// --- 纯净生产环境配置：直接连通您的真实云数据库 ---
const firebaseConfig = {
  apiKey: "AIzaSyCC0N73Sen2e3eUL_DjcpG2-1qVVNwCFS0",
  authDomain: "singmobility.firebaseapp.com",
  projectId: "singmobility",
  storageBucket: "singmobility.firebasestorage.app",
  messagingSenderId: "986480417385",
  appId: "1:986480417385:web:3c6e42eb3892dd779a20f8"
};

// 初始化 Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

export default function App() {
  // 👇 关键升级 2：如果网址里带有 ?verify=xxx，页面一打开就自动跳到查询页 (Directory)
  const [currentPage, setCurrentPage] = useState(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.has('verify')) return 'directory';
    }
    return 'home';
  });

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [newsList, setNewsList] = useState([]); 
  const [isAdminAuth, setIsAdminAuth] = useState(false);

  const navigateTo = (page) => {
    setCurrentPage(page);
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  // 页面加载：同时实时拉取证书数据和新闻数据
  useEffect(() => {
    const certsRef = collection(db, 'certificates'); 
    const unsubCerts = onSnapshot(certsRef, (snapshot) => {
      const certData = [];
      snapshot.forEach(document => {
        certData.push({ id: document.id, ...document.data() });
      });
      setCertificates(certData);
    }, (error) => console.error("Certs fetch error:", error));

    const newsRef = collection(db, 'news');
    const unsubNews = onSnapshot(newsRef, (snapshot) => {
      const nData = [];
      snapshot.forEach(document => {
        nData.push({ id: document.id, ...document.data() });
      });
      nData.sort((a, b) => new Date(b.date) - new Date(a.date));
      setNewsList(nData);
    }, (error) => console.error("News fetch error:", error));
    
    return () => {
      unsubCerts();
      unsubNews();
    };
  }, []);

  // --- 导航栏组件 ---
  const Navigation = () => (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20">
          <div className="flex items-center cursor-pointer" onClick={() => navigateTo('home')}>
            <img 
              src="/logo.png" 
              alt="Logo" 
              className="h-10 w-auto mr-3 object-contain" 
              onError={(e) => { 
                e.target.style.display = 'none';
                if(e.target.nextSibling) e.target.nextSibling.style.display = 'block';
              }} 
            />
            <ShieldCheck className="h-10 w-10 text-blue-800 mr-3 hidden" />
            <div className="flex flex-col text-left">
              <span className="font-bold text-xl text-blue-900 leading-tight">Singmobility</span>
              <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest leading-none">Technology Surveillance</span>
            </div>
          </div>

          <div className="hidden md:flex items-center space-x-6">
            {['home', 'news', 'about', 'rules', 'directory', 'complaints', 'contact'].map((p) => (
              <button 
                key={p} 
                onClick={() => navigateTo(p)} 
                className={`text-xs font-bold uppercase tracking-wider transition-colors hover:text-blue-800 ${currentPage === p ? 'text-blue-900 border-b-2 border-blue-900' : 'text-gray-500'}`}
              >
                {p === 'directory' ? 'Verification' : p.replace('-', ' ')}
              </button>
            ))}
          </div>

          <div className="flex items-center md:hidden">
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-gray-600 focus:outline-none">
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg absolute w-full">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {['home', 'news', 'about', 'rules', 'directory', 'complaints', 'contact'].map((p) => (
              <button key={p} onClick={() => navigateTo(p)} className="block w-full text-left px-3 py-4 text-sm font-bold text-gray-700 hover:text-blue-800 hover:bg-blue-50 uppercase tracking-widest border-b border-gray-50">
                {p === 'directory' ? 'Verification' : p.replace('-', ' ')}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );

  // --- 首页 (Home) ---
  const Home = () => (
    <div className="flex flex-col min-h-screen">
      <div className="bg-blue-900 text-white py-32 md:py-52 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-black opacity-95"></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10 animate-in fade-in slide-in-from-bottom-8 duration-700">
          <h1 className="text-5xl md:text-8xl font-black mb-8 uppercase tracking-tighter leading-[0.9]">Global Trust<br/>Built Locally</h1>
          <p className="text-lg md:text-2xl text-blue-100 max-w-3xl mx-auto mb-16 italic font-medium">Singapore ISO 17065 Compliance Excellence in Technology Surveillance.</p>
          <button onClick={() => navigateTo('directory')} className="bg-white text-blue-900 px-10 py-4 rounded-2xl font-black uppercase tracking-widest shadow-2xl hover:scale-105 active:scale-95 transition-all flex items-center mx-auto">
            <Search className="w-5 h-5 mr-3"/> Verify Certificate
          </button>
        </div>
      </div>
      <div className="py-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 max-w-7xl mx-auto px-6">
        <div className="text-center group"><ShieldCheck className="h-16 w-16 text-blue-800 mx-auto mb-6 group-hover:scale-110 transition-transform" /><h3 className="font-black text-2xl mb-4 text-gray-900 uppercase tracking-tighter">Impartiality</h3><p className="text-gray-500 leading-relaxed">Free from any commercial or financial pressure to ensure objective outcomes.</p></div>
        <div className="text-center group"><FileText className="h-16 w-16 text-blue-800 mx-auto mb-6 group-hover:scale-110 transition-transform" /><h3 className="font-black text-2xl mb-4 text-gray-900 uppercase tracking-tighter">Transparency</h3><p className="text-gray-500 leading-relaxed">Public access to all certification rules, schemes, and verification directories.</p></div>
        <div className="text-center group"><Users className="h-16 w-16 text-blue-800 mx-auto mb-6 group-hover:scale-110 transition-transform" /><h3 className="font-black text-2xl mb-4 text-gray-900 uppercase tracking-tighter">Authority</h3><p className="text-gray-500 leading-relaxed">Senior technical expert team dedicated to rigorous product evaluation.</p></div>
        <div className="text-center group"><Scale className="h-16 w-16 text-blue-800 mx-auto mb-6 group-hover:scale-110 transition-transform" /><h3 className="font-black text-2xl mb-4 text-gray-900 uppercase tracking-tighter">Rights & Duties</h3><p className="text-gray-500 leading-relaxed">Clear definitions of client obligations and rights governing certification.</p></div>
      </div>
    </div>
  );

  // --- 关于我们 (About) ---
  const AboutSection = () => (
    <div className="py-20 max-w-5xl mx-auto px-4 min-h-screen">
      <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-10 uppercase tracking-tighter text-center md:text-left">About Singmobility</h1>
      
      <div className="bg-white p-8 md:p-12 rounded-[2rem] shadow-xl border border-gray-100 mb-12">
        <h2 className="text-2xl font-black text-blue-900 mb-4 uppercase tracking-widest">Our Mission</h2>
        <p className="text-gray-600 leading-relaxed mb-10 text-lg">
          Singmobility Technology Surveillance Pte. Ltd. is a premier certification body based in Singapore. We specialize in providing rigorous, independent, and impartial technology surveillance and certification services in strict compliance with ISO/IEC 17065 standards. Our goal is to foster global trust through local excellence, ensuring products meet the highest safety and quality requirements.
        </p>
        
        <div className="bg-blue-50 p-8 rounded-2xl border border-blue-100">
          <h2 className="text-2xl font-black text-blue-900 mb-4 flex items-center uppercase tracking-widest">
            <ShieldCheck className="w-8 h-8 mr-3 text-blue-700" />
            Commitment to Impartiality
          </h2>
          <p className="text-gray-700 leading-relaxed italic">
            "Singmobility Technology Surveillance Pte. Ltd, its Managing Director, Management Representative and Certification Manager, Staff and others involved in the certification of products of Organization fully understand the importance of impartiality in undertaking its Certification of products. Singmobility Technology Surveillance Pte. Ltd will therefore ensure that in its dealings with clients or potential clients, all employees or other personnel involved in Certification of products are, and will remain, impartial. To ensure that impartiality is both maintained and can be demonstrated, Singmobility Technology Surveillance Pte. Ltd has identified and risk assessed all relationships which may result in a conflict of interest or pose a threat to impartiality."
          </p>
        </div>
      </div>
    </div>
  );

  // --- 认证规则 (Rules) ---
  const RulesSection = () => (
    <div className="py-20 max-w-6xl mx-auto px-4 min-h-screen">
      <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 uppercase tracking-tighter text-center">Certification Rules</h1>
      <p className="text-gray-500 mb-16 text-lg text-center max-w-3xl mx-auto">
        In accordance with ISO/IEC 17065, we provide public access to our certification schemes, evaluation procedures, and the rules governing our certifications.
      </p>

      <div className="grid md:grid-cols-2 gap-8">
        {[
          { title: "Certification Procedure", desc: "Detailed rules for granting, maintaining, extending, and withdrawing certification.", file: "certification-procedure.pdf" },
          { title: "Rights & Duties", desc: "Understand your obligations and rights as a certified client.", file: "rights-and-duties.pdf" },
          { title: "Fee Structure", desc: "General information on the fees charged to applicants and clients.", file: "fee-structure.pdf" },
          { title: "Use of Marks", desc: "Rules governing the correct use of Singmobility certification marks.", file: "marks-usage.pdf" },
          { title: "Confidentiality Agreement", desc: "Our strict commitment to safeguarding your proprietary information and trade secrets.", file: "confidentiality-agreement.pdf" },
          { title: "Disclaimer", desc: "General disclaimers and limitation of liability concerning our certification activities.", file: "disclaimer.pdf" }
        ].map((doc, idx) => (
          <div key={idx} className="bg-white p-8 rounded-[2rem] border border-gray-200 hover:border-blue-500 hover:shadow-2xl transition-all group flex flex-col justify-between">
            <div>
              <FileText className="w-12 h-12 text-blue-900 mb-6 group-hover:scale-110 transition-transform" />
              <h3 className="text-2xl font-black text-gray-900 mb-3">{doc.title}</h3>
              <p className="text-gray-500 mb-8">{doc.desc}</p>
            </div>
            <a href={`/${doc.file}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center bg-gray-50 text-blue-900 font-black text-sm uppercase tracking-widest py-4 rounded-xl group-hover:bg-blue-900 group-hover:text-white transition-colors">
              <Download className="w-5 h-5 mr-2" /> Download PDF
            </a>
          </div>
        ))}
      </div>
    </div>
  );

  // --- 申诉与投诉 (Complaints) ---
  const ComplaintsSection = () => (
    <div className="py-20 max-w-4xl mx-auto px-4 min-h-screen text-center">
      <AlertTriangle className="w-20 h-20 text-blue-900 mx-auto mb-8" />
      <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 uppercase tracking-tighter">Appeals & Complaints</h1>
      <p className="text-gray-500 mb-12 text-lg">
        Singmobility values your feedback and takes all appeals and complaints seriously. Our process ensures a fair, independent, and timely resolution.
      </p>

      <div className="bg-white p-8 md:p-12 rounded-[2rem] shadow-xl border border-gray-100 text-left">
        <h2 className="text-2xl font-black text-blue-900 mb-6 uppercase tracking-widest">Filing Procedure</h2>
        <ol className="list-decimal list-inside space-y-4 text-gray-600 mb-10 text-lg">
          <li>Download and complete our official Appeals & Complaints Form.</li>
          <li>Submit the form via email to our dedicated compliance team.</li>
          <li>You will receive an official acknowledgment within 3 working days.</li>
          <li>An independent committee will review and provide a formal decision within 30 days.</li>
        </ol>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
          <a href="/complaint-form.pdf" target="_blank" rel="noreferrer" className="flex items-center justify-center bg-blue-900 text-white px-8 py-4 rounded-xl font-black uppercase tracking-widest hover:bg-black transition-colors shadow-lg">
            <Download className="w-5 h-5 mr-2" /> Form (PDF)
          </a>
          <a href="mailto:sales@sgmts.com" className="flex items-center justify-center bg-gray-50 text-blue-900 px-8 py-4 rounded-xl font-black uppercase tracking-widest hover:bg-gray-200 transition-colors border-2 border-gray-100">
            <Mail className="w-5 h-5 mr-2" /> Email Us
          </a>
        </div>
      </div>
    </div>
  );

  // --- 联系我们 (Contact Us) ---
  const ContactSection = () => (
    <div className="py-20 max-w-5xl mx-auto px-4 min-h-screen text-center">
      <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 uppercase tracking-tighter">Contact Us</h1>
      <p className="text-gray-500 mb-16 text-lg max-w-2xl mx-auto">
        Get in touch with Singmobility Technology Surveillance for any inquiries regarding our certification services.
      </p>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-xl hover:shadow-2xl transition-shadow flex flex-col items-center">
          <div className="bg-blue-50 p-4 rounded-full mb-6">
            <MapPin className="w-8 h-8 text-blue-900" />
          </div>
          <h3 className="text-xl font-black text-gray-900 mb-4 uppercase tracking-widest">Office Address</h3>
          <p className="text-gray-600 leading-relaxed text-center">
            12 Woodlands Square #08-83,<br/>
            Woods Square,<br/>
            Singapore S737715
          </p>
        </div>
        <div className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-xl hover:shadow-2xl transition-shadow flex flex-col items-center">
          <div className="bg-blue-50 p-4 rounded-full mb-6">
            <Mail className="w-8 h-8 text-blue-900" />
          </div>
          <h3 className="text-xl font-black text-gray-900 mb-4 uppercase tracking-widest">Email Us</h3>
          <a href="mailto:sales@sgmts.com" className="text-blue-600 hover:text-blue-900 font-bold text-lg transition-colors mt-2">
            sales@sgmts.com
          </a>
        </div>
        <div className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-xl hover:shadow-2xl transition-shadow flex flex-col items-center">
          <div className="bg-blue-50 p-4 rounded-full mb-6">
            <Phone className="w-8 h-8 text-blue-900" />
          </div>
          <h3 className="text-xl font-black text-gray-900 mb-4 uppercase tracking-widest">Call Us</h3>
          <a href="tel:+6589655448" className="text-blue-600 hover:text-blue-900 font-bold text-lg transition-colors mt-2">
            +65 8965 5448
          </a>
        </div>
      </div>
    </div>
  );

  // --- 新闻板块 (News) ---
  const NewsSection = () => {
    const displayNews = newsList.length > 0 ? newsList : [
      { id: 'default1', date: '2026-04-20', title: 'Singmobility Expands ISO 17065 Scope', excerpt: 'Comprehensive certification surveillance solutions now available for more sectors.' },
      { id: 'default2', date: '2026-04-15', title: 'New Surveillance Standards Released', excerpt: 'Ensuring global safety through updated rigorous evaluation protocols.' }
    ];

    return (
      <div className="py-20 max-w-5xl mx-auto px-4 min-h-screen text-left">
        <h1 className="text-4xl font-black text-gray-900 mb-12 uppercase tracking-tighter">Latest News</h1>
        <div className="space-y-6">
          {displayNews.map(item => (
            <div key={String(item.id)} className="bg-white p-8 rounded-[2rem] border hover:shadow-xl transition flex flex-col md:flex-row justify-between items-start md:items-center group">
              <div className="flex-grow">
                <div className="flex items-center text-blue-600 text-[10px] font-black mb-2 uppercase tracking-widest">
                  <Clock className="w-3 h-3 mr-2" /> {item.date}
                </div>
                <h2 className="text-xl font-bold text-gray-900 group-hover:text-blue-900 transition-colors">{item.title}</h2>
                <p className="text-gray-500 text-sm mt-2">{item.excerpt}</p>
              </div>
              <ChevronRight className="mt-4 md:mt-0 md:ml-8 text-blue-900" />
            </div>
          ))}
        </div>
      </div>
    );
  };

  // --- 查询中心 (Directory) ---
  const Directory = () => {
    const [q, setQ] = useState('');
    const [res, setRes] = useState(null);
    const [done, setDone] = useState(false);

    // 👇 关键处理：只要证书数据加载完毕，且网址有 verify 参数，立刻执行搜索！
    useEffect(() => {
      if (typeof window !== 'undefined' && certificates.length > 0 && !done) {
        const params = new URLSearchParams(window.location.search);
        const verifyId = params.get('verify');
        
        if (verifyId) {
          setQ(verifyId); 
          setDone(true);  
          setRes(certificates.filter(c => 
            String(c.id).toLowerCase() === verifyId.toLowerCase() || 
            String(c.modelNumber || '').toLowerCase() === verifyId.toLowerCase()
          ));
        }
      }
    }, [certificates, done]);

    return (
      <div className="py-20 max-w-5xl mx-auto px-4 min-h-screen text-center">
        <h1 className="text-4xl font-black mb-4 uppercase tracking-tighter">Public Directory</h1>
        <p className="text-gray-500 mb-12 italic">Verify certificate status issued by Singmobility.</p>
        <form onSubmit={(e)=>{
            e.preventDefault(); 
            setDone(true); 
            setRes(certificates.filter(c=>
              String(c.id).toLowerCase().includes(q.toLowerCase()) || 
              String(c.modelNumber || '').toLowerCase().includes(q.toLowerCase())
            ));
          }} className="max-w-2xl mx-auto mb-16 flex gap-3">
          <input type="text" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Cert ID or Model Number..." className="flex-grow p-5 border-2 rounded-2xl outline-none focus:border-blue-900 font-bold shadow-sm" />
          <button type="submit" className="bg-blue-900 text-white px-10 rounded-2xl font-black uppercase tracking-widest hover:bg-black transition-colors shadow-lg">Verify</button>
        </form>
        {done && res && (
          <div className="bg-white border rounded-[2rem] overflow-hidden text-left shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="bg-gray-900 text-white p-4 px-8 text-[10px] font-black uppercase tracking-widest flex justify-between">
              <span>Records Found: {res.length}</span>
              <span>Cloud DB Connected</span>
            </div>
            {res.length > 0 ? res.map(c => (
              <div key={String(c.id)} className="p-8 border-b flex justify-between items-center hover:bg-blue-50/50 transition-colors">
                <div>
                  <div className="text-2xl font-black text-blue-900 tracking-tighter">{String(c.id)}</div>
                  <div className="font-bold text-gray-800">
                    {String(c.company || 'Unknown')} 
                    <span className="text-gray-400 font-normal pl-2">| Model: {String(c.modelNumber || 'N/A')}</span>
                  </div>
                </div>
                <div className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase shadow-sm ${c.status==='Valid'?'bg-green-100 text-green-700':'bg-red-100 text-red-700'}`}>{String(c.status || 'Unknown')}</div>
              </div>
            )) : <div className="p-20 text-center text-gray-400 font-bold uppercase tracking-widest">No Match Found</div>}
          </div>
        )}
      </div>
    );
  };

  // --- 管理后台 (Admin) ---
  const AdminPanel = () => {
    const [p, setP] = useState('');
    const [nc, setNc] = useState({id:'', company:'', modelNumber:'', status:'Valid'});
    const today = new Date().toISOString().split('T')[0];
    const [nn, setNn] = useState({ title: '', excerpt: '', date: today });
    
    // 👇 您的管理员密码
    const SECRET_PASSWORD = 'Singmobility17065';
    
    if(!isAdminAuth) return (
      <div className="py-24 max-w-md mx-auto min-h-screen text-center px-4">
        <div className="bg-white p-12 border rounded-[3rem] shadow-2xl">
          <Lock className="w-12 h-12 mx-auto mb-6 text-blue-900" />
          <h2 className="text-2xl font-black mb-8 uppercase tracking-tighter">Admin Access</h2>
          <input type="password" value={p} onChange={(e)=>setP(e.target.value)} placeholder="Enter Password" onKeyDown={(e)=>e.key==='Enter'&&p===SECRET_PASSWORD&&setIsAdminAuth(true)} className="w-full border-2 bg-gray-50 p-4 rounded-2xl mb-4 text-center font-bold outline-none focus:border-blue-900" />
          <button onClick={()=>p===SECRET_PASSWORD?setIsAdminAuth(true):alert('Wrong Password')} className="w-full bg-blue-900 text-white py-4 rounded-2xl font-black uppercase tracking-widest shadow-xl active:scale-95 transition-all">Unlock</button>
        </div>
      </div>
    );

    const handleSaveCert = async () => {
      if(!nc.id) return;
      try {
        await setDoc(doc(db, 'certificates', nc.id), nc);
        alert('证书保存成功！(Certificate Saved)');
        setNc({id:'', company:'', modelNumber:'', status:'Valid'});
      } catch (e) {
        console.error(e);
        alert('保存失败，请检查权限。');
      }
    };

    const handleDeleteCert = async (id) => {
      if(!window.confirm('Delete this certificate?')) return;
      try { await deleteDoc(doc(db, 'certificates', String(id))); } catch (e) { console.error(e); }
    };

    const handleSaveNews = async () => {
      if(!nn.title || !nn.excerpt) {
        alert('请填写新闻标题和简介！(Please fill title and excerpt)');
        return;
      }
      try {
        const newsId = Date.now().toString();
        await setDoc(doc(db, 'news', newsId), nn);
        alert('新闻发布成功！(News Published)');
        setNn({ title: '', excerpt: '', date: today });
      } catch (e) {
        console.error(e);
        alert('发布失败，请检查权限。');
      }
    };

    const handleDeleteNews = async (id) => {
      if(!window.confirm('Delete this news?')) return;
      try { await deleteDoc(doc(db, 'news', String(id))); } catch (e) { console.error(e); }
    };

    return (
      <div className="py-20 max-w-6xl mx-auto px-4 text-left min-h-screen">
        <div className="flex justify-between items-center mb-12 border-b pb-4">
          <h2 className="text-3xl font-black uppercase tracking-tighter">System Management</h2>
          <button onClick={()=>setIsAdminAuth(false)} className="text-red-500 font-black text-xs uppercase underline flex items-center hover:text-red-700"><LogOut className="w-4 h-4 mr-1 inline"/> Logout</button>
        </div>

        {/* ========== 第一部分：证书管理 ========== */}
        <h3 className="text-xl font-black mb-6 uppercase tracking-tighter text-gray-800">Certificate Management</h3>
        <div className="grid md:grid-cols-3 gap-12 mb-20">
          <div className="bg-white p-8 border rounded-[2.5rem] shadow-sm h-fit">
            <h3 className="font-black mb-6 uppercase text-blue-900 text-sm flex items-center"><Plus className="w-5 h-5 mr-2"/> Add Record</h3>
            <input type="text" placeholder="Certificate ID" value={nc.id} onChange={(e)=>setNc({...nc, id:e.target.value})} className="w-full border-2 bg-gray-50 p-3 mb-4 rounded-xl font-bold text-sm outline-none focus:border-blue-900" />
            <input type="text" placeholder="Client Name" value={nc.company} onChange={(e)=>setNc({...nc, company:e.target.value})} className="w-full border-2 bg-gray-50 p-3 mb-4 rounded-xl font-bold text-sm outline-none focus:border-blue-900" />
            <input type="text" placeholder="Model Number" value={nc.modelNumber} onChange={(e)=>setNc({...nc, modelNumber:e.target.value})} className="w-full border-2 bg-gray-50 p-3 mb-6 rounded-xl font-bold text-sm outline-none focus:border-blue-900" />
            <button onClick={handleSaveCert} className="w-full bg-blue-900 text-white py-4 rounded-xl font-black uppercase tracking-widest hover:bg-black transition-colors">Save Cert</button>
          </div>
          <div className="md:col-span-2 border rounded-[2.5rem] overflow-hidden bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] font-black uppercase text-gray-400 border-b"><tr><th className="p-6 text-left">Client Details</th><th className="p-6 text-right">Actions</th></tr></thead>
              <tbody className="divide-y divide-gray-50">
                {certificates.length === 0 ? (
                  <tr><td colSpan="2" className="p-6 text-center text-gray-400 font-bold uppercase">No records found.</td></tr>
                ) : (
                  certificates.map(c => (
                    <tr key={String(c.id)} className="hover:bg-gray-50/50 transition-colors">
                      <td className="p-6 text-left">
                        <div className="font-black text-blue-900 text-lg tracking-tight">{String(c.id)}</div>
                        <div className="font-bold text-gray-800">{String(c.company)} <span className="text-gray-400 font-normal pl-2">| {String(c.modelNumber || 'N/A')}</span></div>
                      </td>
                      <td className="p-6 text-right">
                        {/* 👇 关键升级 1：这里增加了一个生成高清二维码的按钮 */}
                        <a 
                          href={`https://api.qrserver.com/v1/create-qr-code/?size=800x800&data=https://www.sgmts.com/?verify=${c.id}`} 
                          target="_blank" 
                          rel="noreferrer"
                          title="Generate QR Code"
                          className="text-blue-500 hover:text-blue-800 transition-colors mr-6 inline-block"
                        >
                          <QrCode className="w-5 h-5"/>
                        </a>
                        <button onClick={() => handleDeleteCert(c.id)} className="text-gray-300 hover:text-red-500 transition-colors"><Trash2 className="w-5 h-5 inline-block"/></button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ========== 第二部分：新闻管理 ========== */}
        <div className="border-t border-gray-200 pt-16">
          <h3 className="text-xl font-black mb-6 uppercase tracking-tighter text-gray-800">News Management</h3>
          <div className="grid md:grid-cols-3 gap-12">
            <div className="bg-white p-8 border rounded-[2.5rem] shadow-sm h-fit">
              <h3 className="font-black mb-6 uppercase text-blue-900 text-sm flex items-center"><Plus className="w-5 h-5 mr-2"/> Publish News</h3>
              <input type="date" value={nn.date} onChange={(e)=>setNn({...nn, date:e.target.value})} className="w-full border-2 bg-gray-50 p-3 mb-4 rounded-xl font-bold text-sm outline-none text-gray-500 focus:border-blue-900" />
              <input type="text" placeholder="News Title" value={nn.title} onChange={(e)=>setNn({...nn, title:e.target.value})} className="w-full border-2 bg-gray-50 p-3 mb-4 rounded-xl font-bold text-sm outline-none focus:border-blue-900" />
              <textarea placeholder="Brief Excerpt..." value={nn.excerpt} onChange={(e)=>setNn({...nn, excerpt:e.target.value})} className="w-full border-2 bg-gray-50 p-3 mb-6 rounded-xl font-bold text-sm outline-none focus:border-blue-900 h-32 resize-none" />
              <button onClick={handleSaveNews} className="w-full bg-blue-900 text-white py-4 rounded-xl font-black uppercase tracking-widest hover:bg-black transition-colors">Publish News</button>
            </div>
            <div className="md:col-span-2 border rounded-[2.5rem] overflow-hidden bg-white shadow-sm h-fit max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] font-black uppercase text-gray-400 border-b sticky top-0"><tr><th className="p-6 text-left">News Details</th><th className="p-6 text-right">Delete</th></tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {newsList.length === 0 ? (
                    <tr><td colSpan="2" className="p-6 text-center text-gray-400 font-bold uppercase">No database news yet. Standard fallback is showing on front-end.</td></tr>
                  ) : (
                    newsList.map(n => (
                      <tr key={String(n.id)} className="hover:bg-gray-50/50 transition-colors">
                        <td className="p-6 text-left">
                          <div className="text-[10px] font-black text-blue-600 mb-1 tracking-widest">{n.date}</div>
                          <div className="font-bold text-gray-900 text-base leading-tight">{n.title}</div>
                        </td>
                        <td className="p-6 text-right"><button onClick={() => handleDeleteNews(n.id)} className="text-gray-300 hover:text-red-500 transition-colors"><Trash2 className="w-5 h-5"/></button></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="font-sans min-h-screen flex flex-col bg-white">
      <Navigation />
      <main className="flex-grow">
        {currentPage === 'home' && <Home />}
        {currentPage === 'news' && <NewsSection />}
        {currentPage === 'about' && <AboutSection />}
        {currentPage === 'rules' && <RulesSection />}
        {currentPage === 'directory' && <Directory />}
        {currentPage === 'complaints' && <ComplaintsSection />}
        {currentPage === 'contact' && <ContactSection />}
        {currentPage === 'admin' && <AdminPanel />}
      </main>
      <footer className="bg-white py-16 border-t mt-auto text-gray-400 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6 text-[10px] font-bold uppercase tracking-widest">
          <div className="flex items-center space-x-3"><img src="/logo.png" alt="Logo" className="h-5 w-auto opacity-40 grayscale" onError={(e)=>e.target.style.display='none'} /><span className="text-gray-900">Singmobility Technology Surveillance</span></div>
          <div className="flex items-center space-x-10 text-[9px] font-black uppercase tracking-widest text-gray-400">
             <div className="flex items-center">
              <div className="w-2 h-2 rounded-full mr-2 bg-green-500 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.5)]"></div>
              Server Online
            </div>
            <button onClick={() => navigateTo('admin')} className="p-3 hover:text-blue-900 hover:bg-blue-50 rounded-xl transition-all"><Lock className="w-4 h-4" /></button>
          </div>
        </div>
      </footer>
    </div>
  );
}